import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/products/grid/table';
import {ProductsGridFormattedRow, ProductsGridRawRow} from 'types/products-grid';

import {GridsNavigation} from './GridsNavigation';
import {MarkingCodeFilter} from './MarkingCodeFilter';
import {ControlsWrapper} from './styled';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: ProductsGridRawRow[] | undefined,
        t: TFunction,
    ) => ProductsGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const ProductsGridTable = ({configFunc, dataMapper, exportConfig}: Props) => {
    const {t} = useTranslation();
    useGate(Gate);

    const {data, pageNumber, isLoading, filters, sortings} = useStore(tableState$);
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);

    const headerControls = (
        <ControlsWrapper>
            {/*<TableSearch />*/}
            <MarkingCodeFilter />
            <GridsNavigation />
        </ControlsWrapper>
    );

    return (
        <Table
            columns={columns}
            data={mappedData}
            hasNext={data?.hasNext}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            isLoading={isLoading}
            userConfig={{
                gridName: 'productsGrid',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            headerControls={headerControls}
            exportConfig={exportConfig}
        />
    );
};
